import { createContext, use } from "react";

import { EntityAttribute } from "shared/api/api";
import { useAllAttributes } from "shared/api/attributes/hooks";

import { DEFAULT_ATTRIBUTES_CONTEXT } from "./constants";
import { useAllGroupByRelationIDValues } from "./hooks";
import {
  AllAttributesContextType,
  AttributesContextContent,
  ContextName,
} from "./types";

const AttributesContext = createContext<AllAttributesContextType>(
  DEFAULT_ATTRIBUTES_CONTEXT
);

export const AttributesContextWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data } = useAllAttributes();
  const formattedData = Object.entries(data || {}).reduce(
    (acc, [key, value]) => {
      acc[key as ContextName] = value?.attributes || [];

      return acc;
    },
    {} as Record<ContextName, EntityAttribute[]>
  );

  const groupByValues = useAllGroupByRelationIDValues(formattedData);
  const definedData = data || {};

  const value: AllAttributesContextType = {
    groupByValues,
    attributes: definedData,
  };

  return (
    <AttributesContext.Provider value={value}>
      {children}
    </AttributesContext.Provider>
  );
};

export const useAttributesContext = (
  contextName: ContextName
): AttributesContextContent => {
  const context = use(AttributesContext);

  const attributes = context.attributes[contextName];
  const groupByValues = context.groupByValues[contextName];

  if (!context) {
    throw new Error(`Context for ${contextName} not found`);
  }

  return { attributes: attributes?.attributes, groupByValues };
};
